import Guid from "common/values/guid/guid";
import moment, { Moment } from "moment";

export interface Identifiable {
  id?: Guid | null;
}

export default class PaginatedResponse<T extends Identifiable> {
  data: Array<T>;
  headers: PaginationHeaders;

  private constructor(
    data: Array<T>,
    headers: PaginationHeaders
  ) {
    this.data = data;
    this.headers = headers;
  }

  public get pageIndex(): number {
    return this.headers.pageIndex;
  }
  public get pageSize(): number {
    return this.headers.pageSize;
  }
  public get totalElements(): number {
    return this.headers.totalElements;
  }
  public get totalPages(): number {
    return this.headers.totalPages;
  }
  public get isEmpty(): boolean {
    return this.data.length === 0;
  }
  public get hasPreviousPage(): boolean {
    return this.headers.hasPrev ?? (this.pageIndex > 0);
  }
  public get hasNextPage(): boolean {
    return this.headers.hasNext ?? (this.pageIndex < (this.totalPages - 1));
  }
  public get minCreatedDate(): Moment {
    return moment(this.headers.minCreatedDate);
  }
  public get maxCreatedDate(): Moment {
    return moment(this.headers.maxCreatedDate);
  }

  public appendOrReplaceData(
    newData: T[],
    newHeaders?: PaginationHeaders,
    comparator?: (a: T, b: T) => number
  ): PaginatedResponse<T> {
    const getId = (item: T) => item.id!.value;

    const existingDataMap = new Map<string, T>(
      this.data.map(item => [getId(item), item])
    );

    let newCount = 0;
    for (const item of newData) {
      const id = getId(item);
      if (existingDataMap.has(id)) {
        // replace the old item
        existingDataMap.set(id, item);
      } else {
        // brand-new item
        existingDataMap.set(id, item);
        newCount++;
      }
    }

    const mergedData: T[] = Array.from(existingDataMap.values());

    if (comparator) {
      mergedData.sort(comparator);
    }

    if (newHeaders) {
      return new PaginatedResponse<T>(mergedData, { ...newHeaders });
    }

    const totalElements: number = this.totalElements + newCount;
    const totalPages: number = Math.ceil(totalElements / this.pageSize);

    return new PaginatedResponse<T>(
      mergedData,
      {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        totalElements,
        totalPages,
        hasPrev: this.hasPreviousPage,
        hasNext: this.hasNextPage,
        minCreatedDate: this.headers.minCreatedDate,
        maxCreatedDate: this.headers.maxCreatedDate
      }
    );
  }

  public removeDataById(id: Guid): PaginatedResponse<T> {
    const found: boolean = this.data.some(item => item.id?.isEqualTo(id));
    const newData: T[] = this.data.filter(item => !item.id?.isEqualTo(id));
    const totalElements: number = this.totalElements - (found ? 1 : 0);
    const totalPages: number = Math.ceil(totalElements / this.pageSize);

    return new PaginatedResponse<T>(
      newData,
      {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        totalElements,
        totalPages,
        hasPrev: this.hasPreviousPage,
        hasNext: this.hasNextPage,
        minCreatedDate: this.headers.minCreatedDate,
        maxCreatedDate: this.headers.maxCreatedDate
      }
    );
  }

  public static empty<T extends Identifiable>(): PaginatedResponse<T> {
    return new PaginatedResponse<T>([], {
      pageIndex: 0,
      pageSize: 0,
      totalElements: 0,
      totalPages: 0,
      hasPrev: false,
      hasNext: false,
      minCreatedDate: undefined,
      maxCreatedDate: undefined
    });
  }

  public static parsePaginationHeader(headerValue: string): PaginationHeaders {
    const params = new URLSearchParams(headerValue);
    return {
      pageIndex: Number.parseInt(params.get("pageIndex") ?? "0", 10),
      pageSize: Number.parseInt(params.get("pageSize") ?? "0", 10),
      totalElements: Number.parseInt(params.get("totalElements") ?? "0", 10),
      totalPages: Number.parseInt(params.get("totalPages") ?? "0", 10),
      hasPrev: params.get("hasPrev") === "true",
      hasNext: params.get("hasNext") === "true",
      minCreatedDate: params.get("minCreatedDate") ?? undefined,
      maxCreatedDate: params.get("maxCreatedDate") ?? undefined
    };
  }

  public static from<T extends Identifiable>(data: T[], headers: string): PaginatedResponse<T> {
    const params = PaginatedResponse.parsePaginationHeader(headers);
    return new PaginatedResponse<T>(
      data,
      {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        totalElements: params.totalElements,
        totalPages: params.totalPages,
        hasPrev: params.hasPrev,
        hasNext: params.hasNext,
        minCreatedDate: params.minCreatedDate,
        maxCreatedDate: params.maxCreatedDate
      }
    );
  }
}

export type PaginationHeaders = {
  pageIndex: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasPrev?: boolean;
  hasNext?: boolean;
  minCreatedDate?: string;
  maxCreatedDate?: string;
}
